import { Input } from "../ui/input";
import { firstName } from "../../lib/names";

const ASSESSMENT_CATEGORIES = [
  { key: "participation_level", label: "Attendance" },
  { key: "engagement", label: "Engagement" },
  { key: "communication", label: "Communication" },
  { key: "behavior", label: "Behavior" },
  { key: "attention", label: "Attention" },
  { key: "work_completion", label: "Task Completion" },
];

// Matches the mobile card: label on its own line, four full-width colored score buttons.
const RATING_STYLES = {
  1: { text: "text-red-600", active: "bg-red-600 border-red-600 text-white", border: "border-gray-200" },
  2: { text: "text-orange-500", active: "bg-orange-500 border-orange-500 text-white", border: "border-gray-200" },
  3: { text: "text-blue-600", active: "bg-blue-600 border-blue-600 text-white", border: "border-gray-200" },
  4: { text: "text-green-600", active: "bg-green-600 border-green-600 text-white", border: "border-gray-200" },
};

export const StudentAssessmentCard = ({ student, assessment, onChange, note }) => {
  const dna = !!assessment.did_not_attend;

  const handleFieldChange = (field, value) => {
    onChange({ ...assessment, [field]: value });
  };

  const handleDnaToggle = () => {
    onChange({
      ...assessment,
      did_not_attend: !dna,
      participation_level: null, engagement: null, communication: null,
      behavior: null, attention: null, work_completion: null,
    });
  };

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm transition-colors ${dna ? "bg-orange-50 border-orange-300" : "bg-white border-gray-200"}`}
      data-testid={`student-assessment-card-${student.id}`}
    >
      <div className="flex items-center justify-between border-b pb-2 mb-3">
        <h3 className="text-base font-bold text-[#00305E]">
          {firstName(student)}
          {note ? <span className="ml-2 align-middle text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5" data-testid={`student-note-${student.id}`}>{note}</span> : null}
        </h3>
        <button
          type="button"
          onClick={handleDnaToggle}
          data-testid={`dna-toggle-${student.id}`}
          className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
            dna ? "bg-orange-500 border-orange-500 text-white" : "border-orange-300 text-orange-600 hover:bg-orange-50"
          }`}
        >
          {dna ? "Did Not Attend" : "DNA"}
        </button>
      </div>

      {!dna && (
        <div className="space-y-3">
          {ASSESSMENT_CATEGORIES.map((category) => (
            <div key={category.key}>
              <p className="text-sm font-semibold text-slate-800 mb-1.5">{category.label}</p>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((rating) => {
                  const active = assessment[category.key] === rating;
                  const s = RATING_STYLES[rating];
                  return (
                    <button
                      key={rating}
                      type="button"
                      data-testid={`score-${student.id}-${category.key}-${rating}`}
                      onClick={() => handleFieldChange(category.key, rating)}
                      className={`h-11 rounded-lg border text-base font-bold transition-all ${
                        active ? s.active : `bg-white ${s.border} ${s.text} hover:border-gray-300`
                      }`}
                    >
                      {rating}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        {dna ? (
          <Input
            placeholder="What did the student do instead? (optional)"
            value={assessment.alternate_plan || ""}
            onChange={(e) => handleFieldChange("alternate_plan", e.target.value)}
            className="text-sm bg-white border-orange-200"
            data-testid={`notes-${student.id}`}
          />
        ) : (
          <Input
            placeholder="Notes (optional)"
            value={assessment.notes || ""}
            onChange={(e) => handleFieldChange("notes", e.target.value)}
            className="text-sm bg-slate-50"
            data-testid={`notes-${student.id}`}
          />
        )}
      </div>
    </div>
  );
};
